import { forwardRef } from 'react';
import type { ReactNode, Ref } from 'react';
import { Tooltip } from '../presentation/Tooltip';
import { PopperMenu, PopperMenuItem } from '../Popper';

type TagActionsMenuTag = {
  hidden: boolean;
  parent?: {
    id?: number | null;
  } | null;
};

export type ProjectTagActionsMenuProps = {
  close?: () => void;
  deleteDisabledReason?: ReactNode;
  disableDelete?: boolean;
  disableEdit?: boolean;
  editDisabledReason?: ReactNode;
  onCreateSubtag?: () => void;
  onDelete?: () => void;
  onEdit: () => void;
  onToggleHide: () => void;
  tag: TagActionsMenuTag;
};

const runMenuAction = (close: (() => void) | undefined, action: (() => void) | undefined) => {
  close?.();
  action?.();
};

const MenuItemWithTooltip = ({
  children,
  disabled = false,
  onClick,
  tooltip,
}: {
  children: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  tooltip?: ReactNode;
}) => {
  const item = (
    <div>
      <PopperMenuItem disabled={disabled} onClick={disabled ? undefined : onClick}>
        {children}
      </PopperMenuItem>
    </div>
  );

  if (!tooltip) {
    return item;
  }

  return (
    <Tooltip placement="top" title={tooltip}>
      {item}
    </Tooltip>
  );
};

export const ProjectTagActionsMenu = forwardRef(
  (
    {
      close,
      deleteDisabledReason,
      disableDelete = false,
      disableEdit = false,
      editDisabledReason,
      onCreateSubtag,
      onDelete,
      onEdit,
      onToggleHide,
      tag,
    }: ProjectTagActionsMenuProps,
    ref: Ref<HTMLDivElement>
  ) => {
    return (
      <PopperMenu ref={ref}>
        <MenuItemWithTooltip
          disabled={disableEdit}
          onClick={() => runMenuAction(close, onEdit)}
          tooltip={disableEdit ? editDisabledReason : undefined}
        >
          Edit
        </MenuItemWithTooltip>
        {!tag.parent?.id && onCreateSubtag && (
          <PopperMenuItem onClick={() => runMenuAction(close, onCreateSubtag)}>Add Subtag</PopperMenuItem>
        )}
        <PopperMenuItem onClick={() => runMenuAction(close, onToggleHide)}>
          {tag.hidden ? 'Unhide' : 'Hide'}
        </PopperMenuItem>
        <MenuItemWithTooltip
          disabled={disableDelete}
          onClick={() => runMenuAction(close, onDelete)}
          tooltip={disableDelete ? deleteDisabledReason : undefined}
        >
          Delete
        </MenuItemWithTooltip>
      </PopperMenu>
    );
  }
);

ProjectTagActionsMenu.displayName = 'ProjectTagActionsMenu';